/**
 * Normalizes a pathname for comparison by stripping trailing slashes so that
 * `/dentures` and `/dentures/` are treated as equal. The root path (`/`) is
 * preserved as-is.
 */
export const normalizePathname = (pathname: string) =>
  pathname === "/" ? pathname : pathname.replace(/\/+$/, "");

/**
 * Matches a single configured path pattern against the current pathname.
 *
 * - Plain paths (`/about/real-estate`) require an exact match.
 * - Trailing `/*` opts into a subtree match: `/about/real-estate/*` matches
 *   `/about/real-estate` itself AND any descendant (`/about/real-estate/ak`,
 *   `/about/real-estate/ak/details`), but NOT `/about/real-estate-other`.
 *
 * Matching is trailing-slash agnostic on both the pattern and the pathname.
 */
export const pathMatches = (pattern: string, current: string) => {
  const c = normalizePathname(current);
  if (pattern.endsWith("/*")) {
    const p = normalizePathname(pattern.slice(0, -2));
    return c === p || c.startsWith(`${p}/`);
  }
  return normalizePathname(pattern) === c;
};

/**
 * Returns true when `current` matches any pattern in `patterns` using
 * {@link pathMatches}. `patterns` is typically a loosely-typed config value, so
 * non-array inputs and non-string entries are ignored rather than throwing.
 */
export const matchesAnyPath = (patterns: unknown, current: string): boolean =>
  Array.isArray(patterns) &&
  patterns.some((pattern) => typeof pattern === "string" && pathMatches(pattern, current));
